import type {
  ConversationSummary,
  InboxCounts,
  InboxPage,
  InboxView,
  MessageDetail,
  TakeoverState,
} from './types'

import { api } from '@/lib/api/client'

const base = (ws: string) => `/v1/workspaces/${ws}/conversations`

/**
 * Index signature so this satisfies the client's `Query` type.
 *
 * The client drops undefined and empty values, so an unset filter simply does
 * not appear in the URL — which keeps the query key stable and the cache from
 * fragmenting on `?channel=` versus nothing.
 */
export interface InboxQuery {
  view?: InboxView
  channel?: string
  chatbot_id?: string
  assigned_to?: string
  intent?: string
  search?: string
  cursor?: string
  limit?: number
  [key: string]: string | number | boolean | null | undefined
}

export const inboxApi = {
  list: (ws: string, query: InboxQuery = {}) =>
    api.get<InboxPage>(base(ws), query),

  counts: (ws: string, query: Omit<InboxQuery, 'view' | 'cursor'> = {}) =>
    api.get<InboxCounts>(`${base(ws)}/counts`, query),

  thread: (ws: string, id: string) =>
    api.get<MessageDetail[]>(`${base(ws)}/${id}/messages`),

  reply: (ws: string, id: string, body: { content: string; is_internal?: boolean }) =>
    api.post<MessageDetail>(`${base(ws)}/${id}/messages`, body),

  takeOver: (ws: string, id: string) =>
    api.post<TakeoverState>(`${base(ws)}/${id}/takeover`, {}),

  returnToAi: (ws: string, id: string) =>
    api.delete<TakeoverState>(`${base(ws)}/${id}/takeover`),

  assign: (ws: string, id: string, memberId: string | null) =>
    api.put<ConversationSummary>(`${base(ws)}/${id}/assignee`, { member_id: memberId }),

  setStatus: (ws: string, id: string, status: string) =>
    api.put<{ status: string }>(`${base(ws)}/${id}/status`, { status }),
}
